// Proposition TRAME — CONTRAT des types partagés entre le moteur (shared/proposition), le serveur et l'écran.
// Spécification : docs/SPEC-PROPOSITION-CONCEPTION-2026-10-22.md ; contrat d'API : docs/CONTRAT-API-PROPOSITION.md.
//
// Conventions :
//  - coordonnées « plan » = unités du fond de plan (celles de plans / plan_count_markers / cable_routes) ;
//    le moteur calcule en mètres (échelle validée du plan) et rend les DEUX (x, y en unités du plan ; xM, yM en mètres) ;
//  - puissances en W, longueurs en m, niveaux sonores en dB(A), indices de capacité = nombres du fabricant ;
//  - null = donnée absente : elle produit un point « à saisir » / « à relever », JAMAIS une valeur inventée ;
//  - aucune valeur de fabricant ni de client n'est écrite dans le moteur : tout vient des entrées (catalogue, notice, pièces).
import type { DiametreFrigorifique } from '../frigorifique.ts';

export const SCHEMA_IMPLANTATION = 'trame-implantation/1';
export const VERSION_MOTEUR_IMPLANTATION = 'trame-implantation-2026-10-22.1';
export const MENTION_PROPOSITION = 'Proposé par TRAME — à valider. Pré-dimensionnement d’avant-projet : ne remplace ni le bilan thermique, ni la sélection du fabricant.';
export const MENTION_BESOIN = 'Besoins calculés avec les hypothèses affichées — à confirmer par un bilan thermique (note de calcul) avant exécution.';

export type Pt = [number, number];

/* ─────────────────────────────── usages et types ─────────────────────────────── */

/** Usage d'une pièce tracée (liste fermée, § 2.1) → type du moteur de bilan (shared/cvc/froid.ts TYPES_PIECE). */
export const USAGES_PIECE = [
  ['salle_culte', 'Salle polyvalente, salle de culte', 'salle_culte'],
  ['classe', 'Salle de classe', 'classe'],
  ['salle_reunion', 'Salle de réunion', 'salle_reunion'],
  ['bureau', 'Bureau', 'bureau'],
  ['regie', 'Régie technique (moteur : bureau + équipements saisis)', 'bureau'],
  ['commerce', 'Commerce, accueil', 'commerce'],
  ['restaurant', 'Salle de restaurant', 'restaurant'],
  ['sejour', 'Logement — séjour', 'sejour'],
  ['chambre', 'Logement — chambre', 'chambre'],
  ['non_traite', 'Non traité (circulation, sanitaires, escalier…)', null],
] as const;
export type UsagePiece = typeof USAGES_PIECE[number][0];
export const typeMoteurUsage = (u: UsagePiece): string | null => USAGES_PIECE.find((x) => x[0] === u)?.[2] ?? null;
/** Usages « logement » : proposés sur un système à part si le réglage le demande (§ 3.5). */
export const USAGES_LOGEMENT: readonly UsagePiece[] = ['sejour', 'chambre'];

export const TYPES_PLAFOND = ['faux_plafond_dalles', 'faux_plafond_plein', 'plafond_brut', 'autre'] as const;
export type TypePlafond = typeof TYPES_PLAFOND[number];

export const TYPES_UI = ['cassette_4v', 'cassette_1v', 'cassette_2v', 'gainable_bp', 'gainable_mp', 'gainable_hp', 'murale', 'console', 'plafonnier', 'console_plafonnier'] as const;
export const TYPES_UE = ['ue_drv_2t', 'ue_drv_3t', 'ue_multisplit', 'ue_split'] as const;
export type TypeUi = typeof TYPES_UI[number];
export type TypeUe = typeof TYPES_UE[number];
export type TypeModele = TypeUi | TypeUe | 'boitier';
export const ROLES_MODELE = ['ui', 'ue', 'module_ue', 'boitier'] as const;
export type RoleModele = typeof ROLES_MODELE[number];

/** Famille de placement d'un type d'UI (§ 4). */
export const familleUi = (t: TypeUi): 'cassette' | 'gainable' | 'gainable_hp' | 'murale' | 'console' =>
  t.startsWith('cassette') || t === 'plafonnier' || t === 'console_plafonnier' ? 'cassette'
    : t === 'gainable_hp' ? 'gainable_hp' : t.startsWith('gainable') ? 'gainable' : t === 'murale' ? 'murale' : 'console';

export const TYPES_ELEMENT = ['circulation', 'zone_interdite', 'emplacement_ue', 'ue_imposee', 'passage_vertical', 'evacuation_condensats',
  'tableau_electrique', 'point_gtb', 'percement_interdit'] as const;
export type TypeElement = typeof TYPES_ELEMENT[number];
/** Géométrie attendue par type d'élément : point (1 sommet), polyligne (≥ 2), polygone (≥ 3). */
export const GEOMETRIE_ELEMENT: Record<TypeElement, 'point' | 'polyligne' | 'polygone'> = {
  circulation: 'polygone', zone_interdite: 'polygone', emplacement_ue: 'polygone', ue_imposee: 'point', passage_vertical: 'point',
  evacuation_condensats: 'point', tableau_electrique: 'point', point_gtb: 'point', percement_interdit: 'polyligne',
};

export const STRATEGIES = ['cassettes', 'gainables', 'mixte_usage', 'murales_consoles'] as const;
export type Strategie = typeof STRATEGIES[number];
export const LIBELLES_STRATEGIE: Record<Strategie | 'technicien', string> = {
  cassettes: 'Cassettes d’abord', gainables: 'Gainables d’abord', mixte_usage: 'Mixte par usage', murales_consoles: 'Murales / consoles',
  technicien: 'Proposition du technicien',
};

export const CRITERES = ['bloquants', 'a_verifier', 'couverture', 'cout', 'cuivre_equivalent', 'unites', 'derivations'] as const;
export type Critere = typeof CRITERES[number];
export const LIBELLES_CRITERE: Record<Critere, string> = {
  bloquants: 'points bloquants', a_verifier: 'points « à vérifier »', couverture: 'besoins couverts', cout: 'coût estimé',
  cuivre_equivalent: 'mètres équivalents de cuivre', unites: 'nombre d’unités', derivations: 'nombre de dérivations',
};
/** Ordre initial PROPOSÉ (§ 7.3), à valider par le dirigeant ; modifiable par entreprise (design_settings.criteria_order). */
export const ORDRE_CRITERES_PROPOSE: readonly Critere[] = CRITERES;

/** Ordre de préférence des types d'UI par usage (§ 3.2) — PROPOSITION MÉTIER « à valider », jamais une valeur chiffrée. */
export const PREFERENCES_USAGE_PROPOSEES: Readonly<Record<string, TypeUi[]>> = Object.freeze({
  salle_haute: ['gainable_hp', 'console', 'cassette_4v'],
  salle_culte: ['cassette_4v', 'gainable_mp', 'gainable_bp'],
  classe: ['cassette_4v', 'gainable_mp', 'murale'],
  salle_reunion: ['cassette_4v', 'gainable_mp', 'murale'],
  regie: ['murale', 'cassette_4v', 'console'],
  sejour: ['murale', 'gainable_bp', 'console'],
  chambre: ['murale', 'gainable_bp', 'console'],
  bureau: ['cassette_4v', 'gainable_mp', 'murale'],
  commerce: ['cassette_4v', 'gainable_mp', 'murale'],
  restaurant: ['cassette_4v', 'gainable_mp', 'murale'],
});

/* ─────────────────────────────── entrées du moteur ─────────────────────────────── */

export interface PlanProposition {
  id: string; nom: string;
  /** m par unité du plan (plans.scale_m_per_unit) ; null = pas d'échelle → aucune proposition sur ce plan. */
  echelle: number | null;
  echelleValidee: boolean;
  niveau: number | null;
  /** Altitude du plancher de ce niveau par rapport au niveau 0 (somme des hauteurs d'étage sourcées) ; null = à saisir. */
  altitudeSolM: number | null;
  altitudeSource: string | null;
}

export interface PieceProposition {
  id: string; planId: string; nom: string;
  polygone: Pt[];                 // unités du plan
  usage: UsagePiece; traitee: boolean;
  /** Surface retenue (relevée si saisie, sinon calculée par le serveur) ; null si l'échelle n'est pas validée. */
  surfaceM2: number | null; surfaceSource: string | null;
  hauteurSousPlafondM: number | null;
  plafond: TypePlafond | null; fauxPlafondACreer: boolean;
  /** Trame de dalles saisie : module (mm) et origine (unités du plan) ; null = pas d'alignement. */
  trame: { moduleMm: number; origine: Pt | null } | null;
  plenumMm: number | null;
  occupants: number | null; eclairageW: number | null; equipementsW: number | null; equipementsLatentW: number | null;
  airNeufM3h: number | null; airNeufParCta: boolean; soufflageT: number | null; soufflageHr: number | null;
  acoustiqueDba: number | null; acoustiqueBase: string | null;
  /** Indices des côtés du polygone (côté i = sommet i → sommet i+1) qui sont des murs extérieurs. */
  mursExterieurs: number[];
  validee: boolean;
}

export interface ElementProposition {
  id: string; planId: string; type: TypeElement;
  geometrie: Pt[];                // unités du plan
  attributs: {
    plenumMm?: number | null; plenumSource?: string | null;          // circulation
    motif?: string | null;                                            // zone interdite, percement interdit
    support?: string | null; altitudeM?: number | null; altitudeSource?: string | null; // emplacement / UE imposée / évacuation
    lienId?: string | null; nature?: string | null;                   // passage vertical (paire), évacuation
    repere?: string | null;                                           // tableau électrique
    traversee?: 'interdite' | 'avec_calfeutrement' | null;            // percement interdit
  };
}

export interface DegagementsModele {
  murMm?: number | null; entreUnitesMm?: number | null; plafondMm?: number | null; solMm?: number | null;
  lateralMm?: number | null; facadeMm?: number | null;
  /** UE : dégagement par côté, avec la condition de la notice (ex. « obstacles sur 2 côtés »). */
  avantMm?: number | null; arriereMm?: number | null; gaucheMm?: number | null; droiteMm?: number | null; dessusMm?: number | null;
  condition?: string | null;
}

export interface ModeleProposition {
  id: string; fabricant: string; gamme: string; reference: string; role: RoleModele; type: TypeModele; fluide: string | null; demo: boolean;
  indice: number | null; froidW: number | null; chaudW: number | null; sensibleW: number | null; conditions: string | null;
  /** Tables de correction de la notice (jsonb) — non appliquées en V1 : la puissance reste « nominale non corrigée ». */
  corrections: unknown | null;
  debitAirM3h: Record<string, number> | null; pressionMinPa: number | null; pressionMaxPa: number | null;
  porteeM: number | null; couverture: { forme: 'carre' | 'rectangle' | 'disque'; dimensionsM: number[] } | null;
  dimensionsMm: { l: number | null; p: number | null; h: number | null } | null; panneauMm: number | null; plenumMinMm: number | null;
  reglagesHauteur: { reglage: string; hMinM: number; hMaxM: number }[] | null;
  poseMinM: number | null; poseMaxM: number | null;
  degagements: DegagementsModele | null;
  sonMinDba: number | null; sonMaxDba: number | null; sonConditions: string | null;
  raccordLiquide: DiametreFrigorifique | null; raccordGaz: DiametreFrigorifique | null;
  condensats: { diametreMm: number | null; pompeIntegree: boolean | null; relevageMm: number | null; penteMinPct: number | null; coteSortieMm?: number | null } | null;
  alimentation: { tensionV: number | null; phases: number | null; courantMaxA: number | null; protectionMaxA: number | null; mode: 'depuis_ue' | 'separee' | 'commune_ui' | null } | null;
  bus: { libelle: string | null; topologie: 'chainage' | 'libre' | null; longueurMaxM: number | null; polarise: boolean | null } | null;
  tauxMinPct: number | null; tauxMaxPct: number | null; uiMax: number | null; noticeId: string | null;
  /** Prix d'achat HT (décimal en chaîne) du price_item relié ; null = « prix à renseigner ». */
  prix: string | null; ouvrage: string | null;
  valide: boolean;
}

export interface RegleDiametre {
  id: string; troncon: 'principal_par_ue' | 'entre_derivations' | 'vers_ui' | 'boitier_vers_ui';
  modeleUe: string | null;
  indiceMin: number | null; indiceMinInclus: boolean; indiceMax: number | null; indiceMaxInclus: boolean;
  gaz: DiametreFrigorifique | null; liquide: DiametreFrigorifique | null; refoulement: DiametreFrigorifique | null;
  /** Majoration telle qu'écrite dans la notice : appliquée seulement si la plus grande longueur équivalente dépasse le seuil. */
  majoration: { longueurEquivalenteMinM: number; liquide: DiametreFrigorifique | null; gaz: DiametreFrigorifique | null } | null;
  page: number | null;
}

export interface KitDerivation {
  id: string; type: 'joint' | 'collecteur' | 'ue_multi'; reference: string;
  indiceMin: number | null; indiceMinInclus: boolean; indiceMax: number | null; indiceMaxInclus: boolean;
  departsMax: number | null; equivalentM: number | null; prix: string | null; page: number | null;
}

export interface NoticeProposition {
  id: string; fabricant: string; gamme: string; fluide: string; typeSysteme: string | null; document: string; page: number; valide: boolean;
  maxReelleM: number | null; maxEquivalenteM: number | null; maxTotaleM: number | null; maxApresDerivationM: number | null;
  maxUiDerivationM: number | null; maxH1M: number | null; maxH2M: number | null; maxH3M: number | null;
  jointEquivalentM: number | null; collecteurEquivalentM: number | null; collecteurAutorise: boolean | null;
  collecteurSansDerivationAval: boolean | null; minDroitEntreJointsMm: number | null;
  coudeEquivalent: Record<string, number> | null;
  diametres: RegleDiametre[]; kits: KitDerivation[];
}

export interface ReglagesProposition {
  ordreCriteres: Critere[];
  preferencesUsage: Record<string, TypeUi[]>;
  /** null = critère non appliqué (aucune valeur fournie par TRAME). */
  surpuissanceMaxPct: number | null;
  logementSepare: boolean;
  routageSansPlenum: boolean;
  /** Prix au mètre des tubes (achat HT) par désignation en pouces ; absent = « coût incomplet ». */
  prixTubes: { pouces: string; prix: string | null }[];
  valide: boolean;
}

/** Saisie du bilan thermique du dossier (shared/cvc/projet.ts) : seule source des besoins (aucune seconde méthode). */
export type BilanEntree = unknown;

export interface UniteTechnicien {
  id: string; repere: string; planId: string; x: number; y: number; modeleId: string | null; libelle: string;
}
export interface LiaisonTechnicien {
  id: string; nom: string; planId: string; points: Pt[];
  liquide: DiametreFrigorifique | null; gaz: DiametreFrigorifique | null;
  uniteExterieure: string | null; unitesInterieures: string[]; derivations: { point: number; reference: string | null }[];
  /** Longueur réelle calculée par le serveur (tracé × échelle + verticale) ; null = non calculable. */
  longueurM: number | null;
}
export interface PropositionTechnicienEntree { unites: UniteTechnicien[]; liaisons: LiaisonTechnicien[] }

export interface EntreeProposition {
  plans: PlanProposition[];
  pieces: PieceProposition[];
  elements: ElementProposition[];
  /** Catalogue de la gamme retenue (UI, UE, boîtiers), validé ou non (un modèle non validé n'est jamais proposé). */
  modeles: ModeleProposition[];
  notice: NoticeProposition | null;
  reglages: ReglagesProposition;
  bilan: BilanEntree | null;
  /** Variantes demandées (1 à 3). */
  strategies: Strategie[];
  /** Restreindre aux modèles du devis (identifiants) ; null = toute la gamme. */
  modelesAutorises: string[] | null;
  technicien: PropositionTechnicienEntree | null;
  /** Longueur verticale de chaque paire de passages (id de l'élément → m), calculée par le serveur (verticaleEtages). */
  verticales: Record<string, { metres: number | null; source: string }>;
}

/* ─────────────────────────────── sortie du moteur ─────────────────────────────── */

export type NiveauPoint = 'bloquant' | 'a_verifier' | 'ok';
export interface PointControle {
  id: string; niveau: NiveauPoint; code: string; message: string;
  /** Règle appliquée (paragraphe de la spécification) et source de la limite (notice, page) quand il y en a une. */
  regle: string; source: string | null;
  cible: { type: 'piece' | 'unite' | 'troncon' | 'systeme' | 'ue' | 'proposition' | 'condensat' | 'liaison'; id: string } | null;
  position: { planId: string; x: number; y: number } | null;
  valeur?: number | null; limite?: number | null;
}

export interface BesoinPiece {
  pieceId: string; nom: string; traitee: boolean;
  froidW: number | null; chaudW: number | null; sensibleW: number | null; shr: number | null;
  /** Données manquantes qui rendent le besoin incomplet (libellés). */
  incomplet: string[];
  alertes: { code: string; message: string }[];
  airNeufCtaFroidW: number | null; airNeufCtaChaudW: number | null;
  mention: string;
}

export interface AffectationPiece {
  pieceId: string; type: TypeUi | null; modeleId: string | null; reference: string | null; nombre: number;
  installeFroidW: number | null; installeChaudW: number | null; installeSensibleW: number | null;
  couverture: { froid: number | null; chaud: number | null; sensible: number | null };
  surpuissancePct: number | null;
  /** Part de la surface de la pièce couverte (zone couverte connue) ; null = homogénéité non vérifiée. */
  tauxSurfaceCouverte: number | null;
  explication: string[];
}

export interface UniteProposee {
  id: string; repere: string; systemeId: string | null; pieceId: string; planId: string; modeleId: string; reference: string;
  type: TypeUi; indice: number | null;
  x: number; y: number; xM: number; yM: number; orientationDeg: number;
  /** Altitude de pose par rapport au niveau 0 (m) ; null = à saisir. */
  altitudeM: number | null;
  froidW: number | null; chaudW: number | null; sensibleW: number | null;
  /** Emprise (panneau ou unité) et dégagement de la notice, en unités du plan, pour le dessin. */
  emprise: Pt[] | null; degagement: Pt[] | null; zoneCouverte: Pt[] | null;
  emplacement: 'piece' | 'plenum_piece' | 'plenum_circulation' | 'mur' | 'sol';
  murIndex: number | null;
}

export interface BoucheProposee { id: string; uniteId: string; planId: string; x: number; y: number; indicative: boolean }
export interface GaineProposee { id: string; uniteId: string; planId: string; trace: Pt[]; longueurM: number }

export interface GroupeExterieur {
  id: string; systemeId: string; repere: string; modeleId: string; reference: string; indice: number | null;
  planId: string; x: number; y: number; xM: number; yM: number; orientationDeg: number;
  altitudeM: number | null; emprise: Pt[] | null; degagement: Pt[] | null; imposee: boolean;
}

export interface NoeudReseau {
  id: string; systemeId: string; type: 'ue' | 'derivation' | 'collecteur' | 'ui' | 'passage';
  planId: string; x: number; y: number; uniteId?: string | null;
  kit?: { kitId: string | null; reference: string | null; type: 'joint' | 'collecteur'; nombre: number; sommeIndicesAval: number | null } | null;
}

export interface TronconReseau {
  id: string; systemeId: string; de: string; vers: string; planId: string;
  trace: Pt[];                     // unités du plan
  longueurPlaneM: number | null; verticaleM: number | null; longueurReelleM: number | null; longueurEquivalenteM: number | null;
  coudes: number; traverseesMurs: number; traverseesCoupeFeu: number; exterieurM: number;
  sommeIndicesAval: number | null; uisAval: string[];
  categorie: 'principal' | 'entre_derivations' | 'vers_ui';
  liquide: DiametreFrigorifique | null; gaz: DiametreFrigorifique | null; majore: boolean; plafonne: boolean;
  regleId: string | null; page: number | null;
}

export interface CondensatPropose {
  id: string; uniteId: string; evacuationId: string | null; planId: string; trace: Pt[];
  longueurM: number | null; deniveleNecessaireM: number | null; deniveleDisponibleM: number | null;
  pompe: 'non' | 'integree' | 'relevage_a_prevoir' | 'a_verifier';
}

export interface LigneElectrique {
  id: string; type: 'alimentation_ue' | 'alimentation_ui'; tableauId: string | null; cibleId: string; planId: string;
  trace: Pt[]; longueurM: number | null; mention: string;
}

export interface BusPropose {
  systemeId: string; libelle: string | null; ordre: string[]; longueurM: number | null; longueurMaxM: number | null;
  topologie: 'chainage' | 'libre' | null; traces: { planId: string; points: Pt[] }[];
}

export interface SystemePropose {
  id: string; repere: string; nom: string; pieces: string[]; uis: string[]; logement: boolean;
  modeleUeId: string | null; referenceUe: string | null; sommeIndices: number; tauxRaccordementPct: number | null;
  besoinFroidW: number; besoinChaudW: number | null; simultaneite: number;
  motif: string;
  longueurs: {
    totaleM: number | null; plusLongueReelleM: number | null; plusLongueEquivalenteM: number | null; apresPremiereDerivationM: number | null;
    h1M: number | null; h2M: number | null; h3M: number | null; uiDerivationMaxM: number | null; plusLongueUi: string | null;
  };
  /** Unités desservies pour la fiche système (§ 5.7) : surface et hauteur issues de la pièce tracée. */
  unites: { repere: string; modele: string; indice: number | null; pieceId: string; surfaceM2: number | null; hauteurM: number | null }[];
  /** Charge estimée (calculerSysteme) et contrôles réglementaires : remplis par le serveur après le moteur. */
  charge?: { complet: boolean; grammes: string | null; teqCo2: string | null; manques: string[]; concentration: string | null } | null;
}

export interface CriteresProposition {
  bloquants: number; aVerifier: number;
  couvertureMin: number | null; surpuissanceTotalePct: number | null; toutCouvert: boolean;
  unites: { ui: number; ue: number; total: number };
  cuivre: { reelM: number | null; equivalentM: number | null; parDiametre: { cle: string; libelle: string; circuit: 'liquide' | 'gaz'; metres: number }[]; derivations: number; traverseesMurs: number };
  condensats: { metres: number | null; pompes: number };
  cout: { complet: boolean; totalHt: string | null; manquants: string[] };
  acoustiqueMargeMinDb: number | null;
  chargeG: string | null;
}

export interface VarianteProposition {
  id: string; strategie: Strategie | 'technicien'; libelle: string; origine: 'trame' | 'technicien';
  besoins: BesoinPiece[]; affectations: AffectationPiece[];
  systemes: SystemePropose[]; unites: UniteProposee[]; bouches: BoucheProposee[]; gaines: GaineProposee[];
  groupesExterieurs: GroupeExterieur[]; noeuds: NoeudReseau[]; troncons: TronconReseau[];
  condensats: CondensatPropose[]; electricite: LigneElectrique[]; bus: BusPropose[];
  points: PointControle[]; criteres: CriteresProposition; recevable: boolean;
  /** Empreinte du contenu (sert à ne pas afficher deux fois une variante identique). */
  empreinteContenu: string;
}

/** `explicationSansCout` : même phrase construite sans aucun montant (servie aux comptes sans droit aux coûts). */
export interface LigneClassement { rang: number; varianteId: string; libelle: string; explication: string; explicationSansCout: string }

export interface ResultatProposition {
  schema: typeof SCHEMA_IMPLANTATION; versionMoteur: string;
  mentions: string[];
  /** Points communs à toutes les variantes (entrées non validées, échelle, gamme…). */
  pointsEntrees: PointControle[];
  variantes: VarianteProposition[];
  classement: LigneClassement[];
  /** Variantes calculées mais non affichées car identiques à une autre (§ 7.4). */
  doublons: { strategie: Strategie; identiqueA: string }[];
  entreesValidees: boolean;
}
